import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

import OkCancelModal from '@/shared/components/OkCancelModal';
import Checkbox from '@/shared/components/input/Checkbox';
import TimeInput from '@/shared/components/input/TimeInput';
import { CapacityTaskSelect } from '@/shared/components/input/filterInputs/FilterCapacityTaskSelect';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import { useGetSubtaskTemplates } from '@/shared/hooks/task/useGetSubtaskTemplates';
import { useBulkUpdateDeviations } from '@/shared/hooks/task/useBulkUpdateDeviations';

import messages from './deviationOverviewMessages';

const MAIN_TASK_OPTION = '__main__';

const BulkUpdateDeviationsModal = ({
  show,
  onClose,
  onSuccess,
  invoiceStreamIds,
  defaultMainTaskTemplateId,
}) => {
  const intl = useIntl();
  const { bulkUpdateDeviations } = useBulkUpdateDeviations();

  const [mainTaskTemplateId, setMainTaskTemplateId] = useState(defaultMainTaskTemplateId);
  const [target, setTarget] = useState(MAIN_TASK_OPTION);
  const [reset, setReset] = useState(false);
  const [durationMinutes, setDurationMinutes] = useState(null);
  const [propagate, setPropagate] = useState(false);

  const { data: subtasks } = useGetSubtaskTemplates(mainTaskTemplateId);

  // (Re)initialise when the modal opens; pre-fill the main task from the screen filter.
  useEffect(() => {
    if (!show) return;
    setMainTaskTemplateId(defaultMainTaskTemplateId);
    setTarget(MAIN_TASK_OPTION);
    setReset(false);
    setDurationMinutes(null);
    setPropagate(false);
  }, [show, defaultMainTaskTemplateId]);

  const onMainTaskChange = value => {
    setMainTaskTemplateId(value || undefined);
    setTarget(MAIN_TASK_OPTION);
  };

  const onResetChange = checked => {
    setReset(checked);
    if (checked) setDurationMinutes(null);
  };

  const durationValid = reset || (typeof durationMinutes === 'number' && durationMinutes >= 0);
  const canSave = !!mainTaskTemplateId && durationValid;

  const save = async () => {
    const includeMainTask = target === MAIN_TASK_OPTION;
    const job = await bulkUpdateDeviations({
      invoiceStreamIds,
      mainTaskTemplateId,
      includeMainTask,
      subtaskTemplateIds: includeMainTask ? [] : [target],
      reset,
      executionDuration: reset ? null : durationMinutes,
      propagate,
    });

    onSuccess(job);
  };

  return (
    <OkCancelModal
      title={intl.formatMessage(messages.UpdateExecutionDuration)}
      show={show}
      onClose={onClose}
      onSave={save}
      disabled={!canSave}
    >
      <div className="form-horizontal">
        <div className="form-group">
          <label className="control-label col-sm-4">
            <FormattedMessage {...messages.MainTask} />
          </label>
          <div className="col-sm-8">
            <CapacityTaskSelect
              initialValue={mainTaskTemplateId}
              onChange={onMainTaskChange}
              placeholder={intl.formatMessage(messages.MainTask)}
            />
          </div>
        </div>

        <div className="form-group">
          <label className="control-label col-sm-4">
            <FormattedMessage {...messages.SelectTargetTasks} />
          </label>
          <div className="col-sm-8">
            <select
              className="form-control"
              value={target}
              onChange={e => setTarget(e.target.value)}
              disabled={!mainTaskTemplateId}
            >
              <option value={MAIN_TASK_OPTION}>{intl.formatMessage(messages.OnlyMainTask)}</option>
              {(subtasks || []).map(s => (
                <option key={s.id} value={s.id}>
                  {intl.formatMessage(listItemMsg('taskTemplate', s.id, s.name))}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="control-label col-sm-4">
            <FormattedMessage {...messages.SetExecutionDurationTo} />
          </label>
          <div className="col-sm-8">
            <TimeInput
              value={durationMinutes || 0}
              disabled={reset}
              onChange={setDurationMinutes}
            />
          </div>
        </div>

        <div className="form-group">
          <div className="col-sm-offset-4 col-sm-8">
            <Checkbox
              value={reset}
              text={intl.formatMessage(messages.ResetToTemplateValue)}
              onClick={onResetChange}
            />
          </div>
        </div>

        <div className="form-group">
          <div className="col-sm-offset-4 col-sm-8">
            <Checkbox
              value={propagate}
              text={intl.formatMessage(messages.PropagateToPlannedTasks)}
              onClick={checked => setPropagate(checked)}
            />
          </div>
        </div>
      </div>
    </OkCancelModal>
  );
};

BulkUpdateDeviationsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  invoiceStreamIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  defaultMainTaskTemplateId: PropTypes.string,
};

BulkUpdateDeviationsModal.defaultProps = { defaultMainTaskTemplateId: undefined };

export default BulkUpdateDeviationsModal;
